import { useEffect, useState } from "react";
import { Loader2, Ban, Users, MapPin } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { PageLoader } from "@/components/brand/PageLoader";
import { supabase } from "@/lib/supabase";
import { env } from "@/lib/env";
import { formatMoneyMinor } from "@/lib/money";

type StatusFilter = "all" | "pending" | "confirmed" | "cancelled" | "completed";

interface MatchRow {
  id: string;
  status: string;
  target_players: number;
  unit_price_minor: number;
  platform_fee_minor: number;
  currency: string;
  cancelled_reason: string | null;
  confirmed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  court: {
    name: string;
    format: string;
    venue: { venue_name: string } | null;
  } | null;
  slot: { starts_at: string; ends_at: string } | null;
  active_count: { count: number }[] | null;
}

/**
 * Admin → Matches. Cross-venue match list with the only operation an
 * admin actually needs: force-cancel a match (releases holds + refunds
 * captured charges + reverses our application fee).
 *
 * Filter buttons up top let you slice by status; defaults to
 * confirmed+pending so the noisy stuff stays visible. Cancelled and
 * completed lists are still one click away.
 */
export function AdminMatches() {
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [matches, setMatches] = useState<MatchRow[] | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);

  async function load() {
    setMatches(null);
    // Disambiguate the slot embed - there are two FKs between
    // matches and availability_slots (matches.slot_id → slots.id,
    // and slots.match_id → matches.id) so PostgREST needs the
    // constraint name to know which direction we want.
    let query = supabase
      .from("matches")
      .select(`
        id, status, target_players, unit_price_minor, platform_fee_minor,
        currency, cancelled_reason, confirmed_at, cancelled_at, created_at,
        court:courts (
          name, format,
          venue:venue_applications ( venue_name )
        ),
        slot:availability_slots!matches_slot_id_fkey ( starts_at, ends_at ),
        active_count:match_participants ( count )
      `)
      .order("created_at", { ascending: false })
      .limit(100);
    if (filter !== "all") {
      query = query.eq("status", filter);
    }
    const { data, error } = await query;
    if (error) {
      toast.error(error.message);
      setMatches([]);
      return;
    }
    setMatches((data ?? []) as MatchRow[]);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function cancel(matchId: string) {
    const reason = window.prompt(
      "Reason for cancelling this match? (Visible in audit log; max 200 chars)",
      "",
    );
    if (reason === null) return; // user hit cancel on the prompt
    if (
      !window.confirm(
        "Force-cancel this match? Every authorised hold gets released and every captured payment gets refunded. The slot is freed back to open. This cannot be undone.",
      )
    ) {
      return;
    }
    setCancelling(matchId);
    const { data: sess } = await supabase.auth.getSession();
    const jwt = sess?.session?.access_token;
    if (!jwt) {
      setCancelling(null);
      return toast.error("Session expired - sign in again.");
    }
    try {
      const res = await fetch(
        `${env.supabaseUrl}/functions/v1/match-admin-cancel`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ match_id: matchId, reason }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(json?.error ?? `Cancel failed (${res.status})`);
        return;
      }
      toast.success(
        `Cancelled. ${json.released_holds} hold(s) released, ${json.refunded_charges} refund(s)${
          json.stripe_failures ? `, ${json.stripe_failures} Stripe failure(s)` : ""
        }.`,
      );
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Network error");
    } finally {
      setCancelling(null);
    }
  }

  return (
    <div>
      <header className="mb-6">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Admin · Matches
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">Match moderation</h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Every match across every venue. Cancel a match here when you
          need to nuke it from orbit - releases holds, refunds captured
          charges, reverses our application fee, and frees the slot.
          Logged to the audit trail with whatever reason you type in.
        </p>
      </header>

      <div className="mb-5 flex flex-wrap gap-2">
        {(["all", "pending", "confirmed", "cancelled", "completed"] as StatusFilter[]).map(
          (s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${
                filter === s
                  ? "bg-volt text-volt-foreground"
                  : "border border-border bg-card text-muted-foreground hover:border-volt/40 hover:text-foreground"
              }`}
            >
              {s}
            </button>
          ),
        )}
      </div>

      {matches === null ? (
        <PageLoader />
      ) : matches.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center">
          <p className="text-sm font-semibold">No matches in this view</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Try a different filter, or wait for a real game to land.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {matches.map((m) => {
            const active = m.active_count?.[0]?.count ?? 0;
            const when = m.slot ? new Date(m.slot.starts_at) : null;
            const cancellable =
              m.status === "pending" || m.status === "confirmed";
            return (
              <article
                key={m.id}
                className="rounded-2xl border border-border bg-card p-5"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold">
                        {m.court?.venue?.venue_name ?? "Unknown venue"} ·{" "}
                        {m.court?.name} ({m.court?.format})
                      </p>
                      <StatusPill status={m.status} />
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {when
                        ? when.toLocaleString(undefined, {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "no slot"}{" "}
                      · created{" "}
                      {new Date(m.created_at).toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "short",
                      })}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-3 w-3" /> {active} / {m.target_players} seats
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3 w-3" />{" "}
                        {formatMoneyMinor(m.unit_price_minor, m.currency)}
                        /seat (
                        <span className="text-volt">
                          {formatMoneyMinor(m.platform_fee_minor, m.currency)} fee
                        </span>
                        )
                      </span>
                    </div>
                    {m.cancelled_reason && (
                      <p className="mt-2 rounded-md bg-secondary/40 px-2 py-1 font-mono text-[11px] text-muted-foreground">
                        {m.cancelled_reason}
                      </p>
                    )}
                  </div>
                  {cancellable && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => cancel(m.id)}
                      disabled={cancelling !== null}
                    >
                      {cancelling === m.id ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Cancelling…
                        </>
                      ) : (
                        <>
                          <Ban className="h-3.5 w-3.5" />
                          Force-cancel
                        </>
                      )}
                    </Button>
                  )}
                </div>
                <div className="mt-3 border-t border-border pt-2 font-mono text-[10px] text-muted-foreground">
                  {m.id}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === "confirmed"
      ? "bg-volt/15 text-volt"
      : status === "pending"
        ? "bg-[rgba(251,191,36,0.15)] text-[#fbbf24]"
        : status === "cancelled"
          ? "bg-[rgba(248,113,113,0.15)] text-[#f87171]"
          : status === "completed"
            ? "bg-[rgba(125,211,252,0.15)] text-[#7dd3fc]"
            : "bg-secondary text-muted-foreground";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`}
    >
      {status}
    </span>
  );
}

// Suppress unused import warning when Input isn't referenced - keep
// the import so the future "search by id" field can drop in without
// scrolling to find it.
void Input;
